import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatPrice, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Account() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState({ body: "", order_id: "" });

  const loadMsgs = async () => { const { data } = await api.get("/messages"); setMessages(data); };

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    api.get("/orders").then((r) => setOrders(r.data));
    loadMsgs();
  }, [user, nav]);

  if (!user) return null;

  const send = async (e) => {
    e.preventDefault();
    if (!reply.body) return;
    try {
      await api.post("/messages/reply", reply);
      toast.success("Reply sent to the atelier");
      setReply({ body: "", order_id: "" }); loadMsgs();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };

  return (
    <div className="ja-container py-16 md:py-24" data-testid="account-page">
      <div className="text-center mb-12">
        <p className="ja-overline">Your Profile</p>
        <h1 className="ja-serif text-5xl md:text-6xl mt-3 tracking-tight">Welcome, {user.name.split(" ")[0]}.</h1>
        <p className="mt-3 text-sm" style={{ color: "var(--ja-muted)" }}>{user.email} · {user.role}</p>
        <div className="mt-6 flex gap-4 justify-center">
          {user.role === "admin" && <Link to="/admin" className="ja-btn-outline" data-testid="account-admin-link">Admin Dashboard</Link>}
          <button onClick={() => { logout(); nav("/"); }} className="ja-btn-outline" data-testid="logout-button">Sign Out</button>
        </div>
      </div>

      <section className="mt-16">
        <p className="ja-overline mb-6">Order History</p>
        {orders.length === 0 ? (
          <p className="text-sm py-8 border-t border-b" style={{ borderColor: "var(--ja-border)", color: "var(--ja-muted)" }}>No orders yet.</p>
        ) : (
          <div className="border-t" style={{ borderColor: "var(--ja-border)" }}>
            {orders.map((o) => (
              <div key={o.id} className="grid grid-cols-2 md:grid-cols-6 gap-4 py-5 border-b text-sm items-center" style={{ borderColor: "var(--ja-border)" }} data-testid={`order-${o.id}`}>
                <div><p className="ja-serif">{o.order_number}</p><p className="text-xs" style={{ color: "var(--ja-muted)" }}>{new Date(o.created_at).toLocaleDateString()}</p></div>
                <div className="text-xs tracking-[0.2em] uppercase">{o.status}</div>
                <div>{o.items.length} item{o.items.length > 1 ? "s" : ""}</div>
                <div>{formatPrice(o.total, o.currency)}</div>
                <div className="text-xs tracking-[0.2em] uppercase" style={{ color: "var(--ja-gold)" }}>{o.payment_method}</div>
                <div className="flex gap-3 text-xs tracking-[0.2em] uppercase">
                  <Link to={`/order/${o.id}/invoice`} className="underline" data-testid={`invoice-link-${o.id}`}>Invoice</Link>
                  {o.tracking_url && <a href={o.tracking_url} target="_blank" rel="noreferrer" className="underline" style={{ color: "var(--ja-gold)" }}>Track</a>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-16">
        <p className="ja-overline mb-6">Messages from the Atelier</p>
        {messages.length === 0 ? (
          <p className="text-sm py-8 border-t border-b" style={{ borderColor: "var(--ja-border)", color: "var(--ja-muted)" }}>No messages.</p>
        ) : (
          <div className="space-y-4">
            {messages.map((m) => (
              <div key={m.id} className="border p-5 text-sm" style={{ borderColor: "var(--ja-border)", background: m.from_role === "admin" ? "var(--ja-ivory)" : "white" }} data-testid={`message-${m.id}`}>
                <div className="flex justify-between text-xs tracking-[0.2em] uppercase" style={{ color: "var(--ja-muted)" }}>
                  <span>{m.from_role === "admin" ? "Jewels Ashu Atelier" : "You"} · {m.from_name}</span>
                  <span>{new Date(m.created_at).toLocaleString()}</span>
                </div>
                {m.subject && <p className="ja-serif text-lg mt-2">{m.subject}</p>}
                <p className="mt-2 whitespace-pre-wrap">{m.body}</p>
              </div>
            ))}
          </div>
        )}
        <form onSubmit={send} className="mt-6 border p-5" style={{ borderColor: "var(--ja-border)" }}>
          <p className="ja-overline mb-3">Reply to the atelier</p>
          <textarea rows={3} required placeholder="Write a message…" value={reply.body} onChange={(e) => setReply({ ...reply, body: e.target.value })} className="ja-input resize-none" data-testid="reply-body" />
          <div className="flex justify-end mt-4">
            <button className="ja-btn" data-testid="reply-send">Send</button>
          </div>
        </form>
      </section>
    </div>
  );
}

